'use client'

import React, { memo, PropsWithChildren, useCallback, useMemo } from 'react'
import { AssetSummary, SoundsAsset } from '@/lib/assets/types'
import isEqual from 'react-fast-compare'
import { buildPreviewUrl, getAssetKey } from '@/lib/assets/assets'
import { Button, buttonVariants } from '@/components/ui/button'
import { useQuery } from '@tanstack/react-query'
import { cn, downSampleSignal, formatDuration } from '@/lib/utils'
import styles from './music-bar.module.scss'
import useAudioPlayer from '@/lib/hooks/useAudioPlayer'
import { ArrowDownToLineIcon, HeartIcon, LoaderCircleIcon, PauseIcon, PlayIcon } from 'lucide-react'
import SoundWave from '@/components/assets-explorer/sound-wave'
import { features } from '@/lib/config'
import useFavorites from '@/lib/hooks/useFavorites'
import { HeartFilledIcon, StarFilledIcon } from '@radix-ui/react-icons'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { Skeleton } from '@/components/ui/skeleton'
import { useUserContext } from '@/lib/contexts/UserContext'
import Link from 'next/link'
import useAsset, { DownloadOption } from '@/lib/hooks/useAsset'
import { forDetails } from '@/lib/assets/repositories/asset-repository-factory'
import { useUserRealtimeContext } from '@/lib/contexts/UserRealtimeContext'

export type MusicBarProps = {
  asset: AssetSummary
  hideTitle?: boolean
  hideTools?: boolean
  gridLayout?: boolean
  hideDownload?: boolean
  platform?: 'web' | 'portal'
  onClick?: (asset?: AssetSummary) => void
}

const MusicBar = memo(
  ({
    asset,
    children,
    hideTitle = false,
    hideTools = false,
    gridLayout = false,
    hideDownload = false,
    platform = 'web',
  }: PropsWithChildren<MusicBarProps>) => {
    const assetKey = getAssetKey(asset)

    const { data: assetDetails, isLoading: isLoadingAssetDetails } = useQuery({
      queryKey: ['asset', assetKey],
      queryFn: async () => {
        const repository = forDetails()
        return (await repository.getAssetByIdOrSlug({
          assetIdOrSlug: asset.uuid,
          type: 'sc_sounds',
          mainCategoryIdOrSlug: asset.mainCategorySlug!,
        })) as SoundsAsset
      },
      enabled: asset && !!asset.mainCategorySlug,
    })

    const duration = useMemo(() => {
      return assetDetails?.properties?.seconds ?? 0
    }, [assetDetails])

    const formattedDuration = useMemo(() => {
      // Duration should show at least 1 second
      return formatDuration(Math.max(duration, 1))
    }, [duration])

    const bpm = useMemo(() => {
      return assetDetails?.properties?.bpm ?? 0
    }, [assetDetails])

    // Disabled for now
    const assetCategory = useMemo(() => {
      // return assetDetails?.properties?.type
      return undefined
    }, [])

    const { data: normalizedWaveData } = useQuery<number[] | null>({
      queryKey: ['waveform', assetDetails?.uuid],
      queryFn: async () => {
        if (!assetDetails) {
          return null
        }

        const preview = assetDetails.previews?.find((p) => p.type === 'json' && p.variant === 'waveform')

        if (!preview) {
          return null
        }

        const waveFormData: number[] =
          (await fetch(buildPreviewUrl(preview))
            .then((res) => res.json())
            .catch((e) => {
              console.error('Error fetching waveform data:', e)
            })) ?? []

        if (!waveFormData.length) {
          return []
        }

        const maximum = waveFormData.reduce((a: any, b: any) => Math.max(a, b), -Infinity)

        if (!Number.isFinite(maximum) || maximum <= 0) {
          return []
        }

        return waveFormData.map((v: any) => (v * 100) / maximum)
      },
      staleTime: Infinity,
      enabled: !!assetDetails,
    })

    const waveData = useMemo(() => {
      if (!normalizedWaveData?.length) {
        return []
      }

      return downSampleSignal(normalizedWaveData, gridLayout ? 4 : 2)
    }, [gridLayout, normalizedWaveData])

    const audioSrc = useMemo(() => {
      if (!assetDetails) {
        return null
      }

      const preview = assetDetails.previews?.find((p) => p.type === 'audio' && p.variant === 'mp3')

      if (!preview) {
        return null
      }

      return buildPreviewUrl(preview)
    }, [assetDetails])

    const { isPlaying, play, stop, seek, currentTime } = useAudioPlayer(audioSrc)

    const onSeek = useCallback(
      (time: number) => {
        if (!isPlaying) {
          play(time)
        } else {
          seek(time)
        }
      },
      [isPlaying, play, seek]
    )

    const handleToggleAudio = useCallback(() => {
      if (!isPlaying) {
        play()
      } else {
        stop()
      }
    }, [isPlaying, play, stop])

    const { addFavorite, removeFavorite, isFavorite: isFavoriteFn } = useFavorites()
    const isFavorite = isFavoriteFn(assetKey)

    const { isAuthenticating, userToken } = useUserContext()
    const { downloadOptions, assetDetailUrl, isDownloading, download } = useAsset({
      asset: assetDetails ?? undefined,
      userToken,
    })

    // Determine if portal download is possible
    const { state: userStatus, download_method: downloadMethod } = useUserRealtimeContext()
    const isPortalDownload = userStatus === 'online' && downloadMethod === 'portal'

    const handleDownload = useCallback(
      (option: DownloadOption) => {
        if (isDownloading) {
          console.warn('Download already in progress')
          return
        }

        download(option.file, {
          platform: isPortalDownload ? 'portal' : 'web',
        })
      },
      [download, isDownloading, isPortalDownload]
    )

    return (
      <div
        className={cn(styles.MusicBar, {
          //'music-bar tw-grid tw-grid-flow-col tw-items-center tw-justify-start tw-gap-2 ': !gridLayout,
          'music-bar tw-flex tw-h-16 tw-items-center tw-justify-start tw-gap-2': !gridLayout,
          [styles.GridLayout]: gridLayout,
        })}
        onClick={() => {
          handleToggleAudio()
        }}
      >
        {/* Download button */}
        {!hideTools && !gridLayout && (
          <>
            {isDownloading && <LoaderCircleIcon className="tw-h-4 tw-w-4 tw-animate-spin" />}
            {!isDownloading && (
              <HoverCard openDelay={0} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <Link
                    href={downloadOptions[0]?.url ?? '#'}
                    title="Download"
                    className={cn(
                      buttonVariants({
                        variant: 'transparent',
                        size: 'icon',
                      }),
                      '!tw-p-1'
                    )}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleDownload(downloadOptions[0])
                    }}
                  >
                    <ArrowDownToLineIcon className="tw-h-4 tw-w-4" />
                  </Link>
                </HoverCardTrigger>
                <HoverCardContent
                  className="tw-w-auto tw-max-w-min tw-border-0 !tw-bg-[var(--Primary)] tw-animate-in tw-fade-in"
                  side={'top'}
                  sideOffset={-1}
                  align={'center'}
                >
                  <div className="tw-flex tw-flex-col tw-items-start tw-space-y-2">
                    {(isLoadingAssetDetails || isAuthenticating) && (
                      <>
                        <Skeleton className="tw-h-4 tw-w-16" />
                        <Skeleton className="tw-h-4 tw-w-16" />
                        <Skeleton className="tw-h-4 tw-w-16" />
                      </>
                    )}
                    {!isLoadingAssetDetails && !isAuthenticating && (
                      <>
                        {downloadOptions.map((option) => (
                          <Link
                            key={`download-option-${option.url}`}
                            href={option.url}
                            prefetch={false}
                            className="tw-opacity-50 hover:tw-opacity-100"
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              handleDownload(option)
                            }}
                          >
                            <div className="tw-flex tw-items-center tw-space-x-2">
                              <span className="tw-whitespace-nowrap">{option.format?.label}</span>
                              {+(option.level ?? 0) > 0 && <StarFilledIcon className={styles.starIcon} />}
                            </div>
                          </Link>
                        ))}
                      </>
                    )}
                  </div>
                </HoverCardContent>
              </HoverCard>
            )}
          </>
        )}
        <div className={cn(styles.Title, 'tw-mr-auto tw-flex tw-w-full tw-max-w-80 tw-items-center tw-space-x-2')}>
          <Button
            type="button"
            title={isPlaying ? 'Pause' : 'Play'}
            size="icon"
            variant="ghost"
            className="!tw-bg-transparent hover:!tw-bg-transparent hover:!tw-text-[var(--Primary-100)]"
            disabled={!audioSrc}
          >
            {isPlaying && (
              <PauseIcon fill="var(--Primary-100)" className="tw-h-4 tw-w-4" style={{ color: 'var(--Primary-100)' }} />
            )}
            {!isPlaying && (
              <PlayIcon fill="var(--Primary-100)" className="tw-h-4 tw-w-4" style={{ color: 'var(--Primary-100)' }} />
            )}
          </Button>
          {!gridLayout && !!assetDetails?.level && +assetDetails.level > 0 && (
            <StarFilledIcon className="tw-h-4 tw-w-4 tw-text-[var(--Secondary-200)]" />
          )}
          {!hideTitle && (
            <Link
              onClick={(e) => e.stopPropagation()}
              href={assetDetailUrl ?? '#'}
              target="_self"
              className="tw-w-max tw-font-medium hover:tw-underline"
            >
              {asset.name}
            </Link>
          )}
        </div>
        {assetCategory && !gridLayout && (
          <div className="hidden tw-mr-auto tw-min-w-32 tw-text-[var(--Primary-300)] md:tw-flex">{assetCategory}</div>
        )}
        {gridLayout && (
          <div className="tw-flex tw-w-full tw-flex-row tw-items-center tw-justify-end tw-gap-2">
            <div className="tw-text-[var(--Primary-300)]">{formattedDuration}</div>
            {bpm > 0 && <div className="tw-text-[var(--Primary-300)]">{bpm} bpm</div>}
          </div>
        )}
        {!gridLayout && (
          <>
            <div className="tw-min-w-16 tw-text-[var(--Primary-300)]">{bpm ? `${bpm} bpm` : ''}</div>
            <div className="tw-text-[var(--Primary-300)]">{formattedDuration}</div>
          </>
        )}
        {gridLayout && (
          <SoundWave
            waveData={waveData ?? []}
            currentTime={isPlaying ? currentTime : 0}
            duration={duration}
            onSeek={onSeek}
            className="tw-mb-4 tw-mt-4"
          />
        )}
        {!gridLayout && (
          <SoundWave
            waveData={waveData ?? []}
            currentTime={isPlaying ? currentTime : 0}
            duration={duration}
            onSeek={onSeek}
            className="tw-mr-4"
          />
        )}
        {gridLayout && (
          <div className="tw-flex tw-w-full tw-flex-row tw-items-center tw-justify-start">
            {assetCategory && <div className="tw-mr-auto tw-text-[var(--Primary-300)]">{assetCategory}</div>}
            {/* Download button */}
            {gridLayout && !hideDownload && (
              <>
                {isDownloading && <LoaderCircleIcon className="tw-h-4 tw-w-4 tw-animate-spin" />}
                {!isDownloading && (
                  <HoverCard openDelay={0} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <Link
                        href={downloadOptions[0]?.url ?? '#'}
                        title="Download"
                        className={cn(
                          buttonVariants({
                            variant: 'transparent',
                            size: 'icon',
                          }),
                          'tw-flex tw-flex-row tw-items-center tw-justify-start !tw-p-1 tw-animate-in tw-fade-in'
                        )}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleDownload(downloadOptions[0])
                        }}
                      >
                        <ArrowDownToLineIcon className="tw-mb-1 tw-mr-1 tw-h-4 tw-w-4" />
                        {gridLayout && !!assetDetails?.level && +assetDetails.level > 0 && (
                          <StarFilledIcon className="tw-mb-1 tw-mr-1 tw-h-4 tw-w-4 tw-text-[var(--Secondary-200)]" />
                        )}
                        Download
                      </Link>
                    </HoverCardTrigger>
                    <HoverCardContent
                      className={`${gridLayout ? 'tw-max-w-prose' : 'tw-max-w-min'} tw-border-0 !tw-bg-[var(--Primary)]`}
                      side={'top'}
                      sideOffset={-1}
                      align={'start'}
                      alignOffset={!gridLayout ? -16 : 16}
                    >
                      <div className="tw-flex tw-flex-col tw-items-start tw-space-y-2">
                        {(isLoadingAssetDetails || isAuthenticating) && (
                          <>
                            <Skeleton className="tw-h-4 tw-w-16" />
                            <Skeleton className="tw-h-4 tw-w-16" />
                            <Skeleton className="tw-h-4 tw-w-16" />
                          </>
                        )}
                        {!isLoadingAssetDetails && !isAuthenticating && (
                          <>
                            {downloadOptions.map((option) => (
                              <Link
                                key={`download-option-${option.url}`}
                                href={option.url}
                                prefetch={false}
                                className="tw-opacity-50 hover:tw-opacity-100"
                                onClick={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  handleDownload(option)
                                }}
                              >
                                <div className="tw-flex tw-items-center tw-space-x-2">
                                  <span className="tw-whitespace-nowrap">{option.format?.label}</span>
                                </div>
                              </Link>
                            ))}
                          </>
                        )}
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                )}
              </>
            )}
            <div className={cn(styles.Controls, 'tw-space-x-1')}>
              {children}
              {/* Favorite button */}
              {/* eslint-disable-next-line react/jsx-no-undef */}
              {features.favorites && (
                <Button
                  title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                  variant="transparent"
                  size="icon"
                  className="!tw-p-1"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    isFavorite ? removeFavorite(assetKey) : addFavorite(assetKey)
                  }}
                >
                  {isFavorite ? <HeartFilledIcon className="tw-h-4 tw-w-4" /> : <HeartIcon className="tw-h-4 tw-w-4" />}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    )
  },
  isEqual
)

MusicBar.displayName = 'MusicBar'

export { MusicBar }
